/**
 * Whether this browser may send product analytics, and what it may send.
 *
 * THE RULE, in one place so no screen can decide it differently: nothing is
 * collected, stored or loaded until the visitor presses Allow on the consent
 * card, and even then never when
 *   - the browser sends Global Privacy Control or Do Not Track (a standing
 *     No; the card is not shown at all),
 *   - the signed-in account is not eligible (a Loop STUDENT, who may be under
 *     18; cloud-api decides this as `analytics_eligible`),
 *   - an admin is viewing as somebody else (their clicks are not that
 *     person's),
 *   - the page is one somebody reached through a link carrying a secret
 *     (upload links, invites, claims, OAuth, the SSO callback).
 *
 * There is no session replay anywhere, by decision. California's wiretap
 * statute (Penal Code 631) is the reason; see the repo CLAUDE.md cloud-web
 * analytics bullet before turning any recording feature back on.
 */

import { create } from 'zustand';

/** The stored answer. The old value "ok" was an acknowledgement, not consent, so it counts as unanswered. */
export const CONSENT_KEY = 'sv-cloud-consent';
export type ConsentChoice = 'granted' | 'denied';

function readChoice(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

interface ConsentStore {
  choice: ConsentChoice | null;
  set: (choice: ConsentChoice) => void;
  /** Ask again: the "Privacy choices" links call this. */
  reset: () => void;
}

export const useConsentStore = create<ConsentStore>((set) => ({
  choice: readChoice(),
  set: (choice) => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      // Storage disabled: the answer holds for this visit only.
    }
    set({ choice });
  },
  reset: () => {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch {
      // Nothing stored to remove.
    }
    set({ choice: null });
  },
}));

/**
 * Global Privacy Control, or the older Do Not Track. A browser that sends
 * either has said no already. The privacy page promises both.
 */
export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

/**
 * Route prefixes whose URL carries a secret, or whose visitor arrived through
 * one. Analytics never runs on them and the consent card never shows there.
 */
const SENSITIVE_PREFIXES = ['/u/', '/claim/', '/invite/', '/project-invite/', '/oauth/', '/sso/'];

export function isSensitivePath(pathname: string): boolean {
  return SENSITIVE_PREFIXES.some((p) => pathname.startsWith(p));
}

export interface AnalyticsGate {
  choice: ConsentChoice | null;
  gpc: boolean;
  /** False for an account cloud-api marks ineligible. Undefined while signed out. */
  accountEligible: boolean | undefined;
  impersonating: boolean;
  pathname: string;
}

export function analyticsAllowed(g: AnalyticsGate): boolean {
  return (
    g.choice === 'granted' &&
    !g.gpc &&
    g.accountEligible !== false &&
    !g.impersonating &&
    !isSensitivePath(g.pathname)
  );
}

/** Whether to show the consent card at all. It never asks a question whose answer would be ignored. */
export function shouldAskConsent(g: AnalyticsGate): boolean {
  return (
    g.choice === null &&
    !g.gpc &&
    g.accountEligible !== false &&
    !g.impersonating &&
    !isSensitivePath(g.pathname)
  );
}

/**
 * A URL with its query and fragment removed and any path segment after a
 * token-carrying prefix replaced, so no secret reaches an analytics event.
 */
export function sanitizeUrl(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return '';
  }
  return url.origin + sanitizePath(url.pathname);
}

export function sanitizePath(pathname: string): string {
  for (const p of SENSITIVE_PREFIXES) {
    if (pathname.startsWith(p)) return `${p}:redacted`;
  }
  return pathname;
}

/** Property names never sent, whatever a caller passes. */
const DROPPED_PROPS = new Set(['email', '$email', 'name', 'github_handle', '$search']);

/** Any property holding a full URL (`$current_url`, `$referrer`, `$session_entry_url`, ...). */
function isUrlProp(k: string): boolean {
  return k.endsWith('_url') || k.endsWith('referrer');
}

/** Any property holding a path (`$pathname`, `$session_entry_pathname`, `$prev_pageview_pathname`, ...). */
function isPathProp(k: string): boolean {
  return k.endsWith('pathname');
}

export function sanitizeProperties(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (DROPPED_PROPS.has(k)) continue;
    if (isUrlProp(k) && typeof v === 'string') {
      out[k] = sanitizeUrl(v);
    } else if (isPathProp(k) && typeof v === 'string') {
      out[k] = sanitizePath(v);
    } else if ((k === '$set' || k === '$set_once') && v && typeof v === 'object') {
      out[k] = sanitizeProperties(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}
